import apiClient from '../apiClient';
import type { Patient } from '@/types';
import type { MedicalRecord } from './medicalRecordsApi';
import type { Prescription } from '../prescriptionApi';
import type { ClinicLabOrder } from './clinicLabOrdersApi';
import type { ImagingOrder } from '../imagingOrderApi';
import type { Referral } from './referralsApi';
import type { Attachment } from './attachmentsApi';
import type { Invoice } from './billingApi';
import type { MedicalHistory } from './medicalHistoryApi';

/**
 * The chart bundle: one request for what used to be ten.
 *
 * `null` on a section means "you may not read this" — the backend gates
 * each one on the same permission its own endpoint required. `[]` means
 * "there are none". The two must not be conflated: an empty medication
 * list and a hidden medication list look identical on screen but mean
 * opposite things to whoever is reading it.
 */
export interface PatientChartBundle {
  patient: Patient;
  visits: { data: MedicalRecord[]; total: number } | null;
  history: MedicalHistory | null;
  vitals: unknown[] | null;
  prescriptions: Prescription[] | null;
  labOrders: ClinicLabOrder[] | null;
  imagingOrders: ImagingOrder[] | null;
  referrals: Referral[] | null;
  attachments: Attachment[] | null;
  invoices: Invoice[] | null;
  /** Sections whose server-side fetch failed; the client may retry these. */
  failed: string[];
}

/**
 * Query values the bundle is built with. The per-section query keys
 * embed these, so the seeder and the components that read the seeded
 * caches have to agree on them exactly — hence one shared definition
 * rather than a literal at each call site.
 */
export const CHART_BUNDLE = {
  visitLimit: 25,
  vitalsDays: 90,
  listLimit: 300,
} as const;

export const patientChartApi = {
  get: async (patientId: string): Promise<PatientChartBundle> => {
    const response = await apiClient.get(`/patients/${patientId}/chart`, {
      params: {
        visitLimit: CHART_BUNDLE.visitLimit,
        vitalsDays: CHART_BUNDLE.vitalsDays,
        listLimit: CHART_BUNDLE.listLimit,
      },
    });
    return response.data.data as PatientChartBundle;
  },
};
